import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * Centro de Importação — single-stop UI for catalog + sales history upload.
 * Goal: customer sees dashboard light up within seconds of uploading 2 files.
 */
export default function ImportCenter({ onImported }) {
  const productsRef = useRef(null);
  const salesRef = useRef(null);
  const [busy, setBusy] = useState({ products: false, sales: false });
  const [lastResult, setLastResult] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const download = (kind) => {
    // Forces browser to save the file
    const a = document.createElement("a");
    a.href = `${backendUrl}/api/templates/${kind}.csv`;
    a.download = `smartstockai-${kind === "products" ? "produtos" : "vendas"}-modelo.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Download iniciado");
  };

  const handleFile = async (kind, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy((s) => ({ ...s, [kind]: true }));
    const fd = new FormData();
    fd.append("file", file);
    try {
      const endpoint = kind === "products" ? "/products/csv" : "/sales/csv";
      const r = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const data = r.data;
      setLastResult({ kind, ...data, fileName: file.name });
      const label = kind === "products" ? "produtos" : "vendas";
      if (data.errors_total > 0) {
        toast.warning(`${data.inserted} ${label} importados, ${data.errors_total} linhas com problemas`);
      } else {
        toast.success(`${data.inserted} ${label} importados com sucesso!`);
      }
      if (onImported) onImported();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao importar");
      setLastResult({ kind, error: err?.response?.data?.detail || "Erro" });
    } finally {
      setBusy((s) => ({ ...s, [kind]: false }));
      // reset input so the same file can be uploaded again
      e.target.value = "";
    }
  };

  return (
    <div className="p-6 rounded-xl border border-primary/30 bg-card glow-coral mb-6" data-testid="import-center">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold">Centro de importação</h3>
          <p className="text-xs text-muted-foreground">
            Importe seu catálogo e o histórico de vendas — a IA já calcula previsões na hora.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
        {/* Catálogo */}
        <UploadCard
          testid="upload-products"
          icon={FileSpreadsheet}
          title="1. Catálogo de produtos"
          description="SKU, nome, categoria, preço, custo, estoque atual."
          required="sku, name, category, price, cost, stock"
          optional="min_stock, lead_time_days"
          busy={busy.products}
          onUpload={() => productsRef.current?.click()}
          onDownload={() => download("products")}
        />
        <input
          ref={productsRef}
          type="file"
          accept=".csv"
          hidden
          onChange={(e) => handleFile("products", e)}
          data-testid="products-csv-input"
        />

        {/* Vendas */}
        <UploadCard
          testid="upload-sales"
          icon={FileSpreadsheet}
          title="2. Histórico de vendas"
          description="Importe 90+ dias de vendas para a IA prever com precisão máxima."
          required="sku, date, qty"
          optional="price, external_id"
          dateHint="Formato de data: DD/MM/AAAA"
          busy={busy.sales}
          onUpload={() => salesRef.current?.click()}
          onDownload={() => download("sales")}
        />
        <input
          ref={salesRef}
          type="file"
          accept=".csv"
          hidden
          onChange={(e) => handleFile("sales", e)}
          data-testid="sales-csv-input"
        />
      </div>

      {/* Resultado da última importação */}
      {lastResult && !lastResult.error && (
        <div
          className="mt-5 p-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5 flex items-start gap-3"
          data-testid="import-result"
        >
          <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
          <div className="flex-1 text-sm">
            <div className="font-medium text-emerald-300">
              {lastResult.fileName}: {lastResult.inserted} {lastResult.kind === "products" ? "produtos" : "vendas"} importados
            </div>
            {lastResult.errors_total > 0 && (
              <div className="mt-1 text-amber-300">
                {lastResult.errors_total} linhas tiveram problemas — primeiros erros:
                <ul className="mt-1 text-xs text-muted-foreground list-disc list-inside">
                  {lastResult.errors.slice(0, 5).map((e, i) => (
                    <li key={i}>Linha {e.row}: {e.error}</li>
                  ))}
                </ul>
              </div>
            )}
            {lastResult.kind === "sales" && lastResult.inserted > 0 && (
              <div className="mt-2 text-xs text-emerald-300">
                ✨ Vá para o <a href="/app/dashboard" className="underline">Dashboard</a> — suas previsões já estão prontas!
              </div>
            )}
          </div>
        </div>
      )}
      {lastResult?.error && (
        <div className="mt-5 p-4 rounded-lg border border-red-500/30 bg-red-500/5 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
          <div className="text-sm text-red-300">{lastResult.error}</div>
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-border/60 text-xs text-muted-foreground">
        <span className="text-foreground/70 font-medium">Dica:</span> Importe primeiro o catálogo, depois o histórico de vendas.
        A IA cruza os SKUs automaticamente. Aceitamos UTF-8 ou Latin-1, vírgula ou ponto-e-vírgula como separador, e ambos formatos
        de decimal (1.99 ou 1,99). Compatível com Excel BR e Google Sheets.
      </div>
    </div>
  );
}

const UploadCard = ({ testid, icon: Icon, title, description, required, optional, dateHint, busy, onUpload, onDownload }) => (
  <div
    data-testid={testid}
    className="p-5 rounded-lg border border-border bg-secondary/20 hover:border-primary/40 transition-colors"
  >
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-primary" />
      <div className="font-medium text-sm">{title}</div>
    </div>
    <p className="text-xs text-muted-foreground mb-3">{description}</p>
    <div className="text-[11px] text-muted-foreground space-y-0.5 mb-4 font-mono">
      <div><span className="text-foreground/60">obrigatório:</span> {required}</div>
      <div><span className="text-foreground/60">opcional:</span> {optional}</div>
      {dateHint && <div className="text-amber-400/80">{dateHint}</div>}
    </div>
    <div className="flex gap-2">
      <Button
        data-testid={`${testid}-btn`}
        variant="default"
        size="sm"
        disabled={busy}
        onClick={onUpload}
        className="bg-primary hover:bg-primary/90 flex-1"
      >
        <Upload className="w-3.5 h-3.5 mr-1.5" />
        {busy ? "Importando..." : "Importar CSV"}
      </Button>
      <Button
        data-testid={`${testid}-template-btn`}
        variant="outline"
        size="sm"
        onClick={onDownload}
        className="border-border"
      >
        <Download className="w-3.5 h-3.5 mr-1.5" />
        Modelo
      </Button>
    </div>
  </div>
);
